import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { UserPlus, Pencil, Key, Clock } from 'lucide-react';
import type { Therapist, TranslateFn } from '../../types';

interface TherapistsTabProps {
  therapists: Therapist[];
  t: TranslateFn;
  setShowAddTherapist: (show: boolean) => void;
  handleEditTherapist: (therapist: Therapist) => void;
  handleDeactivateTherapist: (id: string) => void;
  handleGenerateOTP: (therapist: Therapist) => void;
  handleToggleAvailability?: (therapistId: string) => void;
}

export const TherapistsTab = ({ therapists, t, setShowAddTherapist, handleEditTherapist, handleDeactivateTherapist, handleGenerateOTP, handleToggleAvailability }: TherapistsTabProps) => (
  <>
    <div className="flex justify-end mb-4">
      <Button onClick={() => setShowAddTherapist(true)} data-testid="add-therapist-btn">
        <UserPlus className="w-4 h-4 mr-2" />
        {t('admin.addTherapist')}
      </Button>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {therapists.map((therapist) => {
        const isAvailable = therapist.availability_status === 'available';
        return (
          <Card key={therapist.id} data-testid={`therapist-card-${therapist.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="relative">
                  <img 
                    src={therapist.photo_url} 
                    alt={therapist.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white ${isAvailable ? 'bg-green-500' : 'bg-red-500'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{therapist.name}</h3>
                  <p className="text-sm text-gray-500">{therapist.phone_number}</p>
                  {therapist.description && (
                    <p className="text-xs text-gray-400 mt-1">{therapist.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2" data-testid={`availability-toggle-${therapist.id}`}>
                    <Switch
                      checked={isAvailable}
                      onCheckedChange={() => handleToggleAvailability?.(therapist.id)}
                      className={isAvailable ? 'data-[state=checked]:bg-green-500' : ''}
                    />
                    <span className={`text-xs font-medium ${isAvailable ? 'text-green-600' : 'text-red-500'}`}>
                      {isAvailable ? t('common.available') : t('common.unavailable')}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs text-gray-500" data-testid={`working-hours-${therapist.id}`}>
                    <Clock className="w-3 h-3" />
                    {therapist.working_hours_start || '09:00'} - {therapist.working_hours_end || '21:00'}
                  </div>
                  {therapist.line_user_id ? (
                    <p className="text-xs text-green-600 mt-1">LINE Connected</p>
                  ) : (
                    <p className="text-xs text-orange-600 mt-1">{t('admin.noLineId')}</p>
                  )}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleEditTherapist(therapist)}
                  data-testid={`edit-therapist-${therapist.id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  {t('common.edit')}
                </Button>
                {therapist.phone_number && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleGenerateOTP(therapist)}
                    data-testid={`generate-otp-${therapist.id}`}
                  >
                    <Key className="w-3 h-3 mr-1" />
                    {t('admin.generateOTP') || 'Generate OTP'}
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => handleDeactivateTherapist(therapist.id)}
                >
                  {t('admin.remove')}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  </>
);
